import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

export interface ToolCatalogItem {
  toolId: string;
  name: string;
  displayName: string;
  description: string;
  source: string;
  riskLevel: string;
  status: string;
  globalEnabled?: boolean;
  allowedActions?: string[];
  inputSchema?: Record<string, unknown>;
}

export interface ToolCallLogItem {
  publicId: string;
  toolId: string;
  source: string | null;
  riskLevel: string | null;
  success: boolean;
  executionTime: number | null;
  statusCode: number | null;
  tokensUsed: number | null;
  cost: number | null;
  input: unknown;
  output: unknown;
  error: string | null;
  requestId: string | null;
  chatSessionId: number | null;
  chatSessionPublicId?: string | null;
  agentPublicId?: string | null;
  agentName?: string | null;
  userPublicId?: string | null;
  userEmail?: string | null;
  timestamp: string;
}

export interface ToolCallStats {
  totalCalls: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  avgExecutionTimeMs: number;
  callsBySource: Record<string, number>;
  callsByTool: Record<string, number>;
}

export interface ExternalDefinitionItem {
  publicId: string;
  name: string;
  description: string | null;
  endpointUrl: string;
  method: string;
  authType: string;
  riskLevel: string;
  hitlMode: string;
  status: string;
  timeoutMs: number;
  ownerUserPublicId: string;
  ownerUserEmail: string;
  agentPublicId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ToolPolicies {
  allowDeleteMethod: boolean;
}

export const fetchToolCatalog = async (): Promise<ToolCatalogItem[]> => {
  const res = await axiosInstance.get<{ success: boolean; items: ToolCatalogItem[] }>('/v1/admin/tools/catalog');
  return res.data.items || [];
};

export const fetchToolCalls = async (params: {
  page: number;
  limit: number;
  toolId?: string;
  source?: string;
  riskLevel?: string;
  success?: string;
  from?: string;
  to?: string;
  requestId?: string;
}) => {
  const res = await axiosInstance.get<{
    success: boolean;
    items: ToolCallLogItem[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }>('/v1/admin/tools/calls', { params });
  return res.data;
};

export const fetchToolCallStats = async (params?: { from?: string; to?: string; toolId?: string }) => {
  const res = await axiosInstance.get<{ success: boolean; stats: ToolCallStats }>('/v1/admin/tools/calls/stats', {
    params,
  });
  return res.data.stats;
};

export const fetchToolCallDetail = async (publicId: string) => {
  const res = await axiosInstance.get<{ success: boolean; item: ToolCallLogItem }>(`/v1/admin/tools/calls/${publicId}`);
  return res.data.item;
};

export const fetchExternalDefinitions = async (params: {
  page: number;
  limit: number;
  status?: string;
  search?: string;
}) => {
  const res = await axiosInstance.get<{
    success: boolean;
    items: ExternalDefinitionItem[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }>('/v1/admin/tools/external-definitions', { params });
  return res.data;
};

export const updateExternalDefinitionStatus = async (
  publicId: string,
  payload: { status: string; reason: string },
) => {
  const res = await axiosInstance.patch(`/v1/admin/tools/external-definitions/${publicId}/status`, payload);
  return res.data;
};

export const updateToolGlobalStatus = async (
  toolId: string,
  payload: { enabled: boolean; reason: string },
) => {
  const res = await axiosInstance.patch<{ success: boolean; item: ToolCatalogItem }>(
    `/v1/admin/tools/catalog/${encodeURIComponent(toolId)}/global-status`,
    payload,
  );
  return res.data.item;
};

export const fetchToolPolicies = async (): Promise<ToolPolicies> => {
  const res = await axiosInstance.get<{ success: boolean; policies: ToolPolicies }>('/v1/admin/tools/policies');
  return res.data.policies;
};

export const updateDeleteMethodPolicy = async (payload: { enabled: boolean; reason: string }) => {
  const res = await axiosInstance.patch<{ success: boolean; policies: ToolPolicies }>(
    '/v1/admin/tools/policies/delete-method',
    payload,
  );
  return res.data.policies;
};

// ============================================
// React Query Hooks
// ============================================

export const useToolCatalog = () => {
  return useQuery({
    queryKey: ['admin-tool-catalog'],
    queryFn: fetchToolCatalog,
  });
};

export const useToolCalls = (params: {
  page: number;
  limit: number;
  toolId?: string;
  source?: string;
  riskLevel?: string;
  success?: string;
  from?: string;
  to?: string;
  requestId?: string;
}) => {
  return useQuery({
    queryKey: ['admin-tool-calls', params],
    queryFn: () => fetchToolCalls(params),
  });
};

export const useToolCallStats = (params?: { from?: string; to?: string; toolId?: string }) => {
  return useQuery({
    queryKey: ['admin-tool-call-stats', params],
    queryFn: () => fetchToolCallStats(params),
  });
};

export const useExternalDefinitions = (params: {
  page: number;
  limit: number;
  status?: string;
  search?: string;
}) => {
  return useQuery({
    queryKey: ['admin-external-definitions', params],
    queryFn: () => fetchExternalDefinitions(params),
  });
};

export const useToolPolicies = () => {
  return useQuery({
    queryKey: ['admin-tool-policies'],
    queryFn: fetchToolPolicies,
  });
};

export const useUpdateExternalDefinitionStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, payload }: { publicId: string; payload: { status: string; reason: string } }) =>
      updateExternalDefinitionStatus(publicId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-external-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-tool-catalog'] });
    },
  });
};

export const useUpdateToolGlobalStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ toolId, payload }: { toolId: string; payload: { enabled: boolean; reason: string } }) =>
      updateToolGlobalStatus(toolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tool-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
  });
};

export const useUpdateDeleteMethodPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { enabled: boolean; reason: string }) => updateDeleteMethodPolicy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tool-policies'] });
    },
  });
};
